import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Popup } from '@atlaskit/popup';
import { ExtractPropsFromDefinitionVersion } from '@atlassiansox/bxp-definition-sdk';
import OriginTracing from '@atlassiansox/origin-tracing';
import Cookies from 'js-cookie';
import { ReactNode, useEffect, useState } from 'react';

import type definition from '../../../definition';
import { CartButton } from '../components/cart-button';
import {
  EntitlementItem,
  EntitlementsPopup
} from '../components/entitlements-popup';
import { ShowAllLink } from '../components/show-all-link';
import { TRACKING_EVENT_NAMES, ACTION_SUBJECT_IDS } from '../utils/constants';
import { containerStyle } from './admin-trial-cart.styles';
import { useEntitlements } from './hooks';
import { useEntitlementListings } from './hooks/use-entitlement-listings';
import FeatureGates from '@atlassian/feature-gate-js-client';

export type ModulePropsV0 = ExtractPropsFromDefinitionVersion<
  typeof definition,
  0
>;

const AdminCartWrapper = ({
  enrolled,
  children
}: {
  enrolled: string;
  children?: ReactNode;
}) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  useEffect(() => {
    FeatureGates.manuallyLogExperimentExposure('wac_cart_a-b_experiment');

    createAnalyticsEvent({
      action: 'exposed',
      actionSubject: 'feature',
      actionSubjectId: ACTION_SUBJECT_IDS.wacCartFeatureExposed,
      attributes: {
        aaid: Cookies.get('__aid_user_id'),
        flagKey: 'wac_cart_a-b_experiment',
        value: enrolled
      }
    }).fire();
  }, []);

  if (enrolled === 'control') {
    return;
  }
  return (
    <div data-testid="admin-trial-cart" css={containerStyle}>
      <div>{children}</div>
    </div>
  );
};
const AdminTrialCart = ({ enrolled }: { enrolled: string } & ModulePropsV0) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [
    entitlements,
    transactionAccountId,
    invoiceGroupId,
    totalEntitlementsCount
  ] = useEntitlements();
  const [
    formattedTotalPrice,
    estimates,
    fetchEntitlementListings,
    isLoadingEntitlementListings,
    fetchEntitlementListingsError
  ] = useEntitlementListings(entitlements ?? []);

  const [popupOpen, setPopupOpen] = useState(false);

  const isProd = location.hostname === 'www.atlassian.com';

  const analyticsAttributes = {
    aaid: Cookies.get('__aid_user_id'),
    transactionAccountId,
    invoiceGroupId,
    numberOfEntitlements: entitlements?.length || 0
  };

  const handleCartClick = () => {
    fetchEntitlementListings();

    createAnalyticsEvent({
      eventName: TRACKING_EVENT_NAMES.wacCartIconClicked,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: ACTION_SUBJECT_IDS.wacCartIcon,
      attributes: {
        popupState: popupOpen ? 'closed' : 'open',
        ...analyticsAttributes
      }
    }).fire();

    if (popupOpen) {
      handleClosePopup();
    } else {
      setPopupOpen(true);
    }
  };

  const handleClosePopup = () => {
    createAnalyticsEvent({
      eventName: TRACKING_EVENT_NAMES.wacCartPopoverClosed,
      action: 'closed',
      actionSubjectId: ACTION_SUBJECT_IDS.wacCartPopoverClosed,
      attributes: analyticsAttributes
    }).fire();
    setPopupOpen(false);
  };

  const getAddPaymentDetailsURL = () => {
    const addPaymentDetailsURL = `https://admin${
      !isProd ? '.stg' : ''
    }.atlassian.com/billing/${transactionAccountId}/${invoiceGroupId}/payment-flow`;

    const origin = new OriginTracing({ product: 'marketing' });

    return origin.addToUrl(addPaymentDetailsURL);
  };

  if (!entitlements?.length) {
    return;
  }

  return (
    <AdminCartWrapper enrolled={enrolled}>
      <Popup
        placement="bottom-end"
        isOpen={popupOpen}
        onClose={handleClosePopup}
        trigger={(triggerProps) => (
          <div {...triggerProps}>
            <CartButton
              onClick={handleCartClick}
              numberOfProductTrials={entitlements?.length || 0}
              analyticsAttributes={analyticsAttributes}
            />
          </div>
        )}
        content={() => (
          <EntitlementsPopup
            addPaymentDetailsURL={getAddPaymentDetailsURL()}
            analyticsAttributes={analyticsAttributes}
            formattedTotalPrice={formattedTotalPrice}
            hasError={fetchEntitlementListingsError}
            isLoading={isLoadingEntitlementListings || estimates.length === 0}
          >
            <div>
              {estimates?.slice(0, 5).map((estimate) => {
                const {
                  entitlementId,
                  siteName,
                  formattedTotalPrice,
                  usersPriceBreakdown,
                  productAndPlanName,
                  productKey,
                  daysLeftInTrial
                } = estimate;

                return (
                  <EntitlementItem
                    key={entitlementId}
                    daysLeft={daysLeftInTrial}
                    productKey={productKey}
                    productAndPlanName={productAndPlanName}
                    siteName={siteName}
                    formattedTotalPrice={formattedTotalPrice}
                    usersPriceBreakdown={usersPriceBreakdown}
                  />
                );
              })}
              {estimates?.length > 5 && (
                <ShowAllLink
                  addPaymentDetailsURL={getAddPaymentDetailsURL()}
                  analyticsAttributes={analyticsAttributes}
                  totalEntitlementsCount={totalEntitlementsCount}
                />
              )}
            </div>
          </EntitlementsPopup>
        )}
      />
    </AdminCartWrapper>
  );
};

export default AdminTrialCart;
